import React, { useState, useEffect } from 'react';
import { ChevronDown, User, Building2, Scale, Building, FileText, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const PROFILE_ICONS = {
  persona_natural: User,
  persona_juridica: Building2,
  abogado: Scale,
  estudio_juridico: Building,
  notario: FileText
};

const PROFILE_COLORS = {
  persona_natural: 'from-blue-500 to-blue-600',
  persona_juridica: 'from-emerald-500 to-emerald-600',
  abogado: 'from-purple-500 to-purple-600',
  estudio_juridico: 'from-amber-500 to-amber-600',
  notario: 'from-rose-500 to-rose-600'
};

const ProfileSelector = ({ onProfileChange, currentProfile = null, showHubs = false }) => {
  const { api } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileHubs, setProfileHubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    if (selectedProfile && showHubs) {
      loadProfileHubs(selectedProfile.type);
    }
  }, [selectedProfile, showHubs]);

  const loadProfiles = async () => {
    try {
      const response = await api.get('/platform/profiles');
      const profileList = response.data.profiles || [];
      setProfiles(profileList);
      
      // Set current profile if provided
      if (currentProfile) {
        const profile = profileList.find(p => p.type === currentProfile);
        if (profile) {
          setSelectedProfile(profile);
        }
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
      // Fallback
      setProfiles([
        { type: 'persona_natural', name: 'Persona Natural', icon: '👤', description: 'Ciudadano particular' },
        { type: 'persona_juridica', name: 'Persona Jurídica', icon: '🏢', description: 'Empresas y organizaciones' },
        { type: 'abogado', name: 'Abogado', icon: '⚖️', description: 'Profesional del derecho' },
        { type: 'estudio_juridico', name: 'Estudio Jurídico', icon: '🏛️', description: 'Firma de abogados' },
        { type: 'notario', name: 'Notario', icon: '📜', description: 'Ministro de fe pública' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadProfileHubs = async (profileType) => {
    try {
      const response = await api.get(`/platform/profiles/${profileType}/hubs`);
      setProfileHubs(response.data.hubs || []);
    } catch (error) {
      console.error('Error loading profile hubs:', error);
      setProfileHubs([]);
    }
  };

  const handleSelect = async (profile) => {
    setSelectedProfile(profile);
    setIsOpen(false);
    
    try {
      await api.post('/platform/user/preferences/profile', { profile_type: profile.type });
      if (onProfileChange) {
        onProfileChange(profile);
      }
    } catch (error) {
      console.error('Error setting profile:', error);
    }
  };

  const IconComponent = selectedProfile ? (PROFILE_ICONS[selectedProfile.type] || User) : User;
  const colorClass = selectedProfile ? PROFILE_COLORS[selectedProfile.type] : 'from-gray-500 to-gray-600';

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-700 rounded-xl h-16 w-full"></div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-4 px-4 py-3 bg-gradient-to-r ${colorClass} hover:opacity-90 rounded-xl transition-all shadow-lg w-full`}
        data-testid="profile-selector"
      >
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
          <IconComponent className="w-5 h-5 text-white" />
        </div>
        <div className="text-left flex-1">
          <p className="text-white font-semibold">
            {selectedProfile?.name || 'Seleccionar perfil'}
          </p>
          <p className="text-white/70 text-sm">
            {selectedProfile?.description || 'Tipo de usuario'}
          </p>
        </div>
        <ChevronDown className={`w-5 h-5 text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="p-3 border-b border-gray-700">
              <p className="text-xs text-gray-400 uppercase tracking-wider">Seleccionar Tipo de Perfil</p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {profiles.map((profile) => {
                const ProfileIcon = PROFILE_ICONS[profile.type] || User;
                const profileColor = PROFILE_COLORS[profile.type] || 'from-gray-500 to-gray-600';
                
                return (
                  <button
                    key={profile.type}
                    onClick={() => handleSelect(profile)}
                    className={`w-full flex items-center gap-4 px-4 py-4 hover:bg-gray-700/50 transition-colors text-left border-b border-gray-700/50 last:border-0 ${
                      selectedProfile?.type === profile.type ? 'bg-gray-700/30' : ''
                    }`}
                    data-testid={`profile-option-${profile.type}`}
                  >
                    <div className={`w-12 h-12 bg-gradient-to-r ${profileColor} rounded-xl flex items-center justify-center shadow-lg`}>
                      <ProfileIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{profile.name}</p>
                      <p className="text-sm text-gray-400">{profile.description}</p>
                      {profile.hub_count && (
                        <p className="text-xs text-blue-400 mt-1">{profile.hub_count} módulos disponibles</p>
                      )}
                    </div>
                    {selectedProfile?.type === profile.type && (
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Show hubs for selected profile */}
      {showHubs && selectedProfile && profileHubs.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          {profileHubs.map((hub) => (
            <button
              key={hub.id}
              className="p-4 bg-gray-800/50 hover:bg-gray-700/50 rounded-xl border border-gray-700 text-left transition-all hover:border-blue-500/50 group"
              data-testid={`hub-${hub.id}`}
            >
              <span className="text-2xl">{hub.icon}</span>
              <p className="text-white font-medium mt-2 text-sm">{hub.name}</p>
              <p className="text-gray-400 text-xs mt-1 line-clamp-2">{hub.description}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileSelector;
